/**
 * The enrichment-JOB vocabulary and the readers for a job row.
 *
 * `@/lib/tina/content-types` owns the eleven `enrichment_*` **content types** and
 * the eleven entity names they strip to; this module owns everything else a job
 * row carries — `mode`, `source`, the park budget, the per-entity deep links,
 * and the projections of the two jsonb blocks the columns come out of. Both the
 * items list (per-job columns), the enrichment queue and the runs list
 * (per-run `trigger_spec.source`) render some of it, so it lives in `src/lib`
 * rather than under any one page.
 *
 * **This is the only copy.** Three separate versions of this file were once
 * written in parallel; they agreed on the day, and nothing kept them agreeing —
 * the park-count readers had already diverged into two different numbers for the
 * same job (see {@link readEnrichmentParkState}). Anything that needs
 * enrichment-job vocabulary imports it from here.
 *
 * Two naming families, and the split is load-bearing:
 *
 * - `read*` projects an untyped jsonb block off a `PipelineItem`. Everything
 *   goes through the `asRecord`/`asString`/`asBoolean` readers, so a malformed
 *   row degrades to `null` instead of throwing inside a table cell, and the
 *   view-models are **snake_case** — see {@link EnrichmentJobRef}.
 * - everything else takes an already-typed input and cannot fail.
 *
 * Every vocabulary value mirrors `src/enrichment/types.py` in Tina —
 * `EnrichmentEntity`, `EnrichmentMode` and `EnrichmentSourceKind`. The label
 * maps are typed `Record<Union, string>` so adding a member to a tuple without
 * labelling it is a compile error.
 */
import {
  CONTENT_TYPE_LABEL,
  ENRICHMENT_CONTENT_TYPES,
  ENRICHMENT_ENTITIES,
  type EnrichmentContentType,
  type EnrichmentEntity,
  enrichmentContentTypeOf,
  enrichmentEntityOf,
} from "@/lib/tina/content-types";
import { asBoolean, asRecord, asString } from "@/lib/tina/value-readers";
import type {
  EnrichmentLowConfidenceProblem,
  HitlContext,
  PipelineItem,
} from "@/types/tina";

/**
 * The one entity vocabulary, re-exported so `@/lib/tina/enrichment-jobs` is a
 * single import for everything about a job row. `content-types.ts` declares it
 * because `entity_type` is a wire contract in its own right (`POST /enrichments`
 * takes it, `GET /events?entity_id=` scopes by it).
 */
export {
  ENRICHMENT_ENTITIES,
  enrichmentContentTypeOf,
  enrichmentEntityOf,
  type EnrichmentEntity,
};

/**
 * `"enrichment_show_genre"` → `"show_genre"`, as a type.
 *
 * Distributes because the checked type is a naked type parameter — inlining the
 * conditional over `EnrichmentContentType` directly would collapse to `never`.
 *
 * Kept as a **tripwire**, not as a second declaration of the union: it is the
 * type of the two derived tables below, and assigning them to the hand-listed
 * `EnrichmentEntity`/`ENRICHMENT_ENTITIES` is what makes a tenth flow added to
 * one half of the vocabulary and not the other a compile error here, rather
 * than a filter that silently omits it.
 */
type EntityOfContentType<T extends string> = T extends `enrichment_${infer E}`
  ? E
  : never;

/**
 * Narrow an untyped entity string to the union, without a cast.
 *
 * Several call sites read an entity type out of loosely-typed payloads — a
 * stage-execution result, a job ref — where it arrives as `string | null`.
 * Asserting it with `as` there would move the risk out of the compiler and into
 * a runtime the API can change under.
 */
export function isEnrichmentEntity(
  value: string | null | undefined,
): value is EnrichmentEntity {
  return (
    value != null && (ENRICHMENT_ENTITIES as readonly string[]).includes(value)
  );
}

/**
 * `"venue"` → `"Venue"`, derived from the one content-type vocabulary rather
 * than hand-written (handoff rule 3). `CONTENT_TYPE_LABEL.enrichment_venue` is
 * `"Venue enrichment"`; on the entity axis the noun alone is what reads, and
 * the column header already says "enrichment".
 */
export const ENRICHMENT_ENTITY_LABEL: Record<EnrichmentEntity, string> =
  Object.fromEntries(
    ENRICHMENT_CONTENT_TYPES.map((contentType) => [
      contentType.slice("enrichment_".length),
      CONTENT_TYPE_LABEL[contentType].replace(/\s+enrichment$/i, ""),
    ]),
  ) as Record<EntityOfContentType<EnrichmentContentType>, string>;

/**
 * Human label for an `entity_type` off the wire. Falls back to the raw value so
 * an entity Tina grows after this deploy renders as itself, not as "—".
 */
export function enrichmentEntityLabel(
  value: string | null | undefined,
): string {
  if (!value) return "—";
  return ENRICHMENT_ENTITY_LABEL[value as EnrichmentEntity] ?? value;
}

/**
 * `{ value, label }` pairs for the eleven entities, in content-type order.
 *
 * Built by stripping the content types rather than by mapping
 * `ENRICHMENT_ENTITIES`, and the `satisfies` closes the loop: between them the
 * two assignments assert each list contains the other, so a twelfth flow added
 * to only one half of the vocabulary is a compile error either way round.
 */
export const ENRICHMENT_ENTITY_OPTIONS: ReadonlyArray<{
  value: EnrichmentEntity;
  label: string;
}> = ENRICHMENT_CONTENT_TYPES.map((contentType) => {
  const value = contentType.slice(
    "enrichment_".length,
  ) as EntityOfContentType<EnrichmentContentType>;
  return { value, label: ENRICHMENT_ENTITY_LABEL[value] };
});

/** The other direction: every hand-listed entity is a content-type suffix. */
ENRICHMENT_ENTITIES satisfies readonly EntityOfContentType<EnrichmentContentType>[];

/**
 * Where a curator goes to look at the entity itself.
 *
 * Hand-maintained because IMS has no route registry to derive it from, and
 * deliberately pointed at routes that **exist**: `artists`/`venues` declare a
 * `show` path on the resource but `App.tsx` only mounts `edit/:id`, so an
 * `/artists/show/…` link would 404. Typed `Record<EnrichmentEntity, …>`, so a
 * twelfth flow is a compile error rather than an unclickable cell.
 */
const ENTITY_HREF: Record<EnrichmentEntity, (id: string) => string> = {
  artist: (id) => `/artists/edit/${id}`,
  venue: (id) => `/venues/edit/${id}`,
  event: (id) => `/events/edit/${id}`,
  genre: (id) => `/genres/edit/${id}`,
  geodata: (id) => `/geodata/edit/${id}`,
  movie: (id) => `/movies/show/${id}`,
  person: (id) => `/people/show/${id}`,
  screening: (id) => `/screenings/show/${id}`,
  play: (id) => `/plays/show/${id}`,
  performance: (id) => `/performances/show/${id}`,
  show_genre: (id) => `/show-genres/show/${id}`,
};

/**
 * The unified enrichment page, `/{resource}/enrichment/:id`.
 *
 * Since MOO-4803 every one of the original nine entities has one (see
 * `src/components/enrichment/enrichment-page.tsx`); MOO-4814 task 17 adds
 * `play`/`performance` to the vocabulary (the route itself is task 18/19's
 * job). This is a full `Record` like `ENTITY_HREF` — a twelfth flow is a
 * compile error here too.
 */
const REVIEW_HREF: Record<EnrichmentEntity, (id: string) => string> = {
  artist: (id) => `/artists/enrichment/${id}`,
  venue: (id) => `/venues/enrichment/${id}`,
  event: (id) => `/events/enrichment/${id}`,
  genre: (id) => `/genres/enrichment/${id}`,
  geodata: (id) => `/geodata/enrichment/${id}`,
  movie: (id) => `/movies/enrichment/${id}`,
  person: (id) => `/people/enrichment/${id}`,
  screening: (id) => `/screenings/enrichment/${id}`,
  play: (id) => `/plays/enrichment/${id}`,
  performance: (id) => `/performances/enrichment/${id}`,
  show_genre: (id) => `/show-genres/enrichment/${id}`,
};

export function enrichmentEntityHref(
  entity: string | null | undefined,
  id: string | null | undefined,
): string | null {
  if (!entity || !id) return null;
  return ENTITY_HREF[entity as EnrichmentEntity]?.(id) ?? null;
}

export function enrichmentReviewHref(
  entity: string | null | undefined,
  id: string | null | undefined,
): string | null {
  if (!entity || !id) return null;
  return REVIEW_HREF[entity as EnrichmentEntity]?.(id) ?? null;
}

/**
 * `GET /events?content_type=enrichment` — "every enrichment job, whatever its
 * entity" (Tina's `ENRICHMENT_CLASS_SENTINEL`, `src/db/queries/events.py:154`).
 *
 * **Not a content type**, which is why it is not in `@/lib/tina/content-types`:
 * the eleven real values are `enrichment_<entity>`, so the bare word cannot
 * collide with a row and `list_events` matches it against the whole class
 * before its equality branch. It is a third explicit opt-in beside
 * `content_type=enrichment_x` and `entity_id=`, so the scrape-only default for
 * every other caller is untouched.
 *
 * Lives here rather than beside either caller because two slices needed it on
 * the same afternoon and each wrote its own copy — the exact failure this module
 * exists to stop. Both had reasoned their way to the same docstring, which is
 * how the duplication survived review on both sides.
 */
export const ENRICHMENT_CLASS_SENTINEL = "enrichment";

/** `EnrichmentMode` — what a job row is asked to do. */
export const ENRICHMENT_MODES = ["both", "enrich", "embed"] as const;
export type EnrichmentMode = (typeof ENRICHMENT_MODES)[number];

export const ENRICHMENT_MODE_LABEL: Record<EnrichmentMode, string> = {
  both: "Enrich + embed",
  enrich: "Enrich",
  embed: "Embed",
};

export function enrichmentModeLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return ENRICHMENT_MODE_LABEL[value as EnrichmentMode] ?? value;
}

/**
 * `EnrichmentSourceKind` — how the job row came to exist. Ten values; the wire
 * form is kebab-case for the multi-word ones.
 *
 * This is the interesting axis on the runs page's Enrichments tab: a run
 * is one intake request or one fan-out batch, so `backend-create` vs `ims` vs
 * `bff-onboarding` is what tells a curator who is generating the volume.
 */
export const ENRICHMENT_SOURCE_KINDS = [
  "backend-create",
  "backend-update",
  "ims",
  "bff-onboarding",
  "bff-tmdb",
  "repair",
  "bypass",
  "hitl-followup",
  "bulk",
  "overflow",
] as const;

export type EnrichmentSourceKind = (typeof ENRICHMENT_SOURCE_KINDS)[number];

export const ENRICHMENT_SOURCE_LABEL: Record<EnrichmentSourceKind, string> = {
  "backend-create": "Backend create",
  "backend-update": "Backend update",
  ims: "IMS",
  "bff-onboarding": "BFF onboarding",
  "bff-tmdb": "BFF TMDb",
  repair: "Dependent repair",
  bypass: "Bypass fan-out",
  "hitl-followup": "HITL follow-up",
  bulk: "Bulk (IMS)",
  overflow: "Tail overflow",
};

export function enrichmentSourceLabel(
  value: string | null | undefined,
): string {
  if (!value) return "—";
  return ENRICHMENT_SOURCE_LABEL[value as EnrichmentSourceKind] ?? value;
}

/**
 * `TINA_ENRICH_HITL_MAX_PARKS`' default — how many low-confidence
 * verdicts one job row absorbs before it closes itself `manual_review`.
 *
 * Mirrored, not read: it is a worker env var and no route exposes it. It never
 * *gates* a retry — Tina enforces the cap itself, and a UI that disabled the
 * button at N-1 would make `park_limit_reached` unreachable, because the closing
 * verdict can only ever come from a curator retry.
 *
 * **An override does make the UI wrong, and not only in the denominator.** This
 * docstring used to claim otherwise; {@link isLastAttempt} and
 * {@link parkRetriesLeft} both derive from this number, so the "this is the last
 * attempt" copy is only as true as the mirror. Tina compares
 * `prior + 1 >= hitl_max_parks()`, so at `TINA_ENRICH_HITL_MAX_PARKS=2` — the
 * smallest override the worker honours, since `0`/`1` fall back to the default —
 * `isLastAttempt(1)` is `false` on the verdict that closes the job, and the panel
 * offers a retry Tina will not give. At `5` it warns of a last attempt three
 * parks early. The claim is correct today only because
 * `infra/terraform/environments/prod.tfvars` sets `enrich_hitl_max_parks = 3`.
 *
 * Left as a mirror rather than fixed, because the honest fix is not IMS-side:
 * the effective limit would have to ride on `EnrichmentLowConfidenceProblem`
 * next to `park_count` (a Tina schema change), and the alternative — dropping
 * the outcome copy — removes the one thing a curator wants to know before
 * pressing Retry. **Change this constant in the same commit as the tfvar.**
 *
 * One name, deliberately: a second constant called `MAX_PARKS` mirrored the
 * same env var until this file absorbed it.
 */
export const ENRICHMENT_PARK_LIMIT = 3;

/** The `enrichment_low_confidence` problem, or null for every other reason. */
export function enrichmentProblemOf(
  context: HitlContext | null | undefined,
): EnrichmentLowConfidenceProblem | null {
  const problem = context?.problem;
  if (!problem || problem.reason !== "enrichment_low_confidence") return null;
  return problem;
}

/**
 * `events.metadata.enrichment` — the job payload, present on every job row from
 * the moment it is queued.
 *
 * **snake_case, and that is the rule for every `read*` view-model here.** These
 * are verbatim projections of what `GET /events` returns: Tina's
 * `EnrichmentJobRef` (`src/enrichment/types.py`) is a Pydantic model dumped as
 * jsonb, and `v_enrichment_jobs` names its columns the same way. Keeping the
 * casing means a reader can diff this against either without a translation
 * table — a camelCase copy of this interface is what the second fork was.
 */
export interface EnrichmentJobRef {
  entity_type: string;
  entity_id: string;
  mode: string | null;
  source: string | null;
  requested_by: string | null;
  /** Bypass fan-out only; the HTTP intake never accepts it. */
  parent_event_id: string | null;
  /** Dry-run: Tina suppressed every write-back. */
  shadow: boolean;
  /**
   * Derived, not a wire key: `metadata.enrichment.park` is present exactly when
   * this row was seeded only to park a proposal the scrape tail produced, and
   * its contents are the seed rather than anything to render.
   */
  park_seed: boolean;
}

/**
 * Read `metadata.enrichment` off a job row. Returns `null` when the row is not
 * an enrichment job, or when the payload is missing the two fields that make it
 * addressable at all (`entity_type` + `entity_id`).
 */
export function readEnrichmentJobRef(
  item: PipelineItem,
): EnrichmentJobRef | null {
  const block = asRecord(asRecord(item.metadata).enrichment);
  // `content_type` is the authority on what kind of row this is; the metadata
  // block is only trusted for the *detail*. A scrape row that happens to carry
  // an `enrichment` key is not a job.
  const entityFromType = enrichmentEntityOf(item.content_type);
  if (!entityFromType) return null;

  const entityId = asString(block.entity_id);
  if (!entityId) return null;

  return {
    entity_type: asString(block.entity_type) ?? entityFromType,
    entity_id: entityId,
    mode: asString(block.mode) ?? null,
    source: asString(block.source) ?? null,
    requested_by: asString(block.requested_by) ?? null,
    parent_event_id: asString(block.parent_event_id) ?? null,
    shadow: asBoolean(block.shadow) ?? false,
    park_seed: Object.keys(asRecord(block.park)).length > 0,
  };
}

/**
 * `events.metadata.enrichment_result` — the `enrich` stage's outcome block.
 *
 * **Not `metadata.enrichment`**: that key holds the job payload above, and
 * `merge_event_jsonb` is a shallow Postgres merge, so writing the outcome there
 * would replace it. Tina writes `enrichment_result`
 * (`shared_stages/enrichment/results.py`).
 */
export interface EnrichmentResultBlock {
  entity_type: string | null;
  entity_id: string | null;
  status: string | null;
  confidence: number | null;
  park_count: number | null;
  /** The give-up flag. The ONLY correct gate for the park-limit notice. */
  park_limit_reached: boolean;
}

export function readEnrichmentResult(
  item: PipelineItem,
): EnrichmentResultBlock | null {
  const block = asRecord(asRecord(item.metadata).enrichment_result);
  if (Object.keys(block).length === 0) return null;
  return {
    entity_type: asString(block.entity_type) ?? null,
    entity_id: asString(block.entity_id) ?? null,
    status: asString(block.status) ?? null,
    confidence: numberOrNull(block.confidence),
    park_count: numberOrNull(block.park_count),
    park_limit_reached: asBoolean(block.park_limit_reached) ?? false,
  };
}

/**
 * Whether the "enrichment gave up" notice applies.
 *
 * Gated on the **flag**, never on `final_outcome === "manual_review"` alone:
 * before the HITL gate existed that outcome was stamped on every gated
 * enrichment, so the looser test would put a scary banner on every old row.
 */
export function isParkLimitReached(item: PipelineItem): boolean {
  return readEnrichmentResult(item)?.park_limit_reached === true;
}

/** The park budget of one job row, as the two places Tina writes it report it. */
export interface EnrichmentParkState {
  /**
   * Parks recorded on this row, or `null` when **neither** source carried one.
   *
   * `null` is not `0`. A row that never reached a verdict has no park history;
   * printing `0/3` for it invents one. See {@link parkAttemptNumber} for the
   * one place absence is deliberately floored, and why.
   */
  count: number | null;
  /** Always {@link ENRICHMENT_PARK_LIMIT}; carried so a cell needs one read. */
  limit: number;
  limitReached: boolean;
}

/**
 * The park budget, read **terminal-first** from the two places Tina writes it.
 *
 * Not from `metadata.enrichment`: `HitlTransition` discards a stage result's
 * metadata entirely, so a counter carried there would never persist. While a
 * job is parked the count lives on `hitl_context.problem`
 * (`EnrichmentLowConfidenceProblem.park_count`); the park-LIMIT exit is not a
 * HITL at all — it closes `ok` with `final_outcome='manual_review'` — so it
 * writes its final count onto `metadata.enrichment_result`.
 *
 * **Precedence is `enrichment_result` then `hitl_context.problem`**, and the
 * authority is Tina's own view, `src/db/views/v_enrichment_jobs.sql`:
 *
 * ```sql
 * COALESCE((metadata -> 'enrichment_result' ->> 'park_count')::int,
 *          (hitl_context -> 'problem'       ->> 'park_count')::int, 0)
 * ```
 *
 * The order is the whole point rather than an accident of writing: a closed row
 * still carries the `hitl_context` of the park *before* the one that closed it,
 * one lower. Reading problem-first printed "Gave up after 2" on a job that gave
 * up after 3 — which is exactly what `/tina/enrichment`'s "Gave up" tab did
 * while `/tina/items` printed `3/3` for the same row.
 *
 * The view coalesces its last step to `0` because a SQL column cannot be
 * absent; this returns `null` there instead, because a table cell can, and "no
 * park history" and "zero parks so far" are different sentences.
 */
export function readEnrichmentParkState(
  item: PipelineItem,
): EnrichmentParkState {
  const result = asRecord(asRecord(item.metadata).enrichment_result);
  const problem = asRecord(asRecord(item.hitl_context).problem);
  const terminal = numberOrNull(result.park_count);
  const parked = numberOrNull(problem.park_count);
  return {
    count: terminal ?? parked,
    limit: ENRICHMENT_PARK_LIMIT,
    limitReached: asBoolean(result.park_limit_reached) ?? false,
  };
}

/**
 * The attempt number to *print beside a parked proposal* — never below 1.
 *
 * The two renderings of an absent count are both correct, for different
 * surfaces, and this is the one that floors:
 *
 * - A **column** ({@link EnrichmentParkState.count} straight through) shows a
 *   dash. The row may never have parked, and `1/3` would assert that it did.
 * - A **HITL panel** is by construction looking at a park, so at least one
 *   exists whether or not the counter survived the jsonb round-trip. `Attempt 1
 *   of 3` is the only value that cannot mislead the curator in front of it.
 *
 * Every piece of park copy — the label, the retries-left tail — goes through
 * this, so the two can no longer disagree about what an absent count means.
 */
export function parkAttemptNumber(
  parkCount: number | null | undefined,
): number {
  return typeof parkCount === "number" && parkCount > 0 ? parkCount : 1;
}

/** "Attempt 2 of 3" — `park_count` counts the park in front of the curator. */
export function parkAttemptLabel(parkCount: number | null | undefined): string {
  return `Attempt ${parkAttemptNumber(parkCount)} of ${ENRICHMENT_PARK_LIMIT}`;
}

/** Attempts still to come after this one — the tail of the "Attempt n of N" copy. */
export function parkRetriesLeft(parkCount: number | null | undefined): number {
  return Math.max(ENRICHMENT_PARK_LIMIT - parkAttemptNumber(parkCount), 0);
}

/**
 * True when another low-confidence result closes the job instead of parking.
 *
 * Tina computes `park_count = prior + 1` and compares it `>=` the limit, so at
 * `limit - 1` the next verdict is terminal.
 */
export function isLastAttempt(parkCount: number | null | undefined): boolean {
  return parkAttemptNumber(parkCount) + 1 >= ENRICHMENT_PARK_LIMIT;
}

/**
 * jsonb numbers arrive as numbers, but a value written by an older deploy (or
 * round-tripped through a text column) can arrive as a numeric string.
 */
function numberOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = asString(value);
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}
